import React from "react";
import { useTranslation } from "react-i18next";
import {
  AdminFeedbackEntry,
  adminFeedbackSenderName,
} from "../../features/admin/models/AdminFeedbackEntry";

const formatDateTime = (date: Date): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);

/** Badge styling per feedback type — wishes read calm, issues read urgent. */
const typeBadgeClass = (type: string): string => {
  switch (type) {
    case "Issue":
      return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
    case "Wish":
      return "bg-primary-95 dark:bg-primary-30 text-primary-40 dark:text-primary-90";
    default:
      return "bg-grey-90 dark:bg-primary-20 text-grey-30 dark:text-grey-70";
  }
};

interface AdminFeedbackRowProps {
  entry: AdminFeedbackEntry;
}

const AdminFeedbackRow: React.FC<AdminFeedbackRowProps> = ({ entry }) => {
  const { t } = useTranslation();
  const from = entry.audience === "Member" ? t("admin.feedback.fromMember") : t("admin.feedback.fromInstructor");
  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-semibold text-grey-10 dark:text-white">
            {adminFeedbackSenderName(entry)}
          </p>
          <p className="truncate text-sm text-grey-40 dark:text-grey-70">{entry.email}</p>
          <p className="truncate text-sm text-grey-40 dark:text-grey-70" data-testid="feedback-from">
            {t("admin.feedback.from")}: {from}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${typeBadgeClass(entry.type)}`}>
            {entry.type}
          </span>
          <span className="text-xs text-grey-50 dark:text-grey-60">
            {formatDateTime(entry.dateCreated)}
          </span>
        </div>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm text-grey-20 dark:text-grey-80">
        {entry.message}
      </p>
    </div>
  );
};

export default AdminFeedbackRow;
